import { useCallback, useEffect, useState } from "react";

import { LocalStorageKey } from "shared/constants/storageKeys";

import {
  deleteLocalStorageData,
  getLocalStorageData,
  setLocalStorageData,
} from "shared/helpers/view";

const LOCAL_STORAGE_EVENT = "app-local-storage-updated";

type LocalStorageUpdateDetail<T> = {
  key: LocalStorageKey;
  value: T;
};

export const useLocalStorage = <T>(key: LocalStorageKey, initialValue: T) => {
  const readValue = useCallback((): T => {
    try {
      const item = getLocalStorageData(key);
      return item ?? initialValue;
    } catch (error) {
      console.error("Error reading localStorage key:", key, error);
      return initialValue;
    }
  }, [key, initialValue]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = useCallback(
    async (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        setLocalStorageData(key, valueToStore);

        window.dispatchEvent(
          new CustomEvent<LocalStorageUpdateDetail<T>>(LOCAL_STORAGE_EVENT, {
            detail: {
              key,
              value: valueToStore,
            },
          })
        );
      } catch (error) {
        console.error("Error setting localStorage key:", key, error);
      }
    },
    [key, readValue]
  );

  const removeValue = useCallback(async () => {
    try {
      deleteLocalStorageData(key);
      setStoredValue(initialValue);

      window.dispatchEvent(
        new CustomEvent<LocalStorageUpdateDetail<T>>(LOCAL_STORAGE_EVENT, {
          detail: {
            key,
            value: initialValue,
          },
        })
      );
    } catch (error) {
      console.error("Error removing localStorage key:", key, error);
    }
  }, [key, initialValue]);

  useEffect(() => {
    setStoredValue(readValue());
  }, [readValue]);

  useEffect(() => {
    const handleCustomStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent<LocalStorageUpdateDetail<T>>;

      if (customEvent.detail?.key === key) {
        setStoredValue(customEvent.detail.value);
      }
    };

    const handleNativeStorageChange = (event: StorageEvent) => {
      if (event.key !== key) {
        return;
      }

      setStoredValue(readValue());
    };

    window.addEventListener(LOCAL_STORAGE_EVENT, handleCustomStorageChange);
    window.addEventListener("storage", handleNativeStorageChange);

    return () => {
      window.removeEventListener(
        LOCAL_STORAGE_EVENT,
        handleCustomStorageChange
      );
      window.removeEventListener("storage", handleNativeStorageChange);
    };
  }, [key, readValue]);

  return [storedValue, setValue, removeValue] as const;
};
